import { useState, type FormEvent, type ReactNode } from 'react';
import { Copy, Download, KeyRound, ShieldCheck, ShieldOff } from 'lucide-react';
import QRCode from 'qrcode';
import { TOTP_DIGITS, type TwoFactorSetupResponse } from '@findmy/shared';
import {
  useDisableTwoFactor,
  useEnableTwoFactor,
  useReplaceRecoveryCodes,
  useSession,
  useStartTwoFactor,
} from '../api/queries';
import { Dialog } from './Dialog';
import { Badge, Button, ErrorText } from './ui';

const inputClass =
  'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-800';

function PasswordField() {
  return (
    <label className="block text-sm font-medium">
      Your password
      <input
        name="password"
        type="password"
        autoComplete="current-password"
        required
        autoFocus
        className={inputClass}
      />
    </label>
  );
}

function CodeField({
  label = 'Code from your app',
  allowRecovery = false,
}: {
  label?: string;
  allowRecovery?: boolean;
}) {
  return (
    <label className="block text-sm font-medium">
      {label}
      <input
        name="code"
        required
        autoComplete="one-time-code"
        inputMode={allowRecovery ? 'text' : 'numeric'}
        pattern={allowRecovery ? undefined : `\\d{${TOTP_DIGITS}}`}
        maxLength={allowRecovery ? 20 : TOTP_DIGITS}
        placeholder={allowRecovery ? '123456 or XXXX-XXXX-XXXX' : '123456'}
        className={`${inputClass} font-mono tracking-widest`}
      />
    </label>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="flex justify-end gap-2">{children}</div>;
}

const field = (event: FormEvent<HTMLFormElement>, name: string) =>
  String(new FormData(event.currentTarget).get(name));

/** Shown once: the codes, with ways to keep them. */
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { data: session } = useSession();
  const [copied, setCopied] = useState(false);
  const text = [
    `Find My recovery codes${session ? ` for ${session.user.email}` : ''}`,
    'Each code works once, instead of a code from your authenticator app.',
    '',
    ...codes,
    '',
  ].join('\n');

  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => setCopied(true));
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'findmy-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm">
        If you lose your phone, each of these codes lets you log in once. <strong>Save them now</strong> (in a
        password manager, or printed); they won’t be shown again.
      </p>
      <ul
        aria-label="Recovery codes"
        className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-3 font-mono text-sm dark:bg-slate-800"
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button onClick={copy}>
          <Copy className="h-4 w-4" aria-hidden /> {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button onClick={download}>
          <Download className="h-4 w-4" aria-hidden /> Download
        </Button>
        <Button variant="primary" onClick={onDone} className="ml-auto">
          I’ve saved them
        </Button>
      </div>
    </div>
  );
}

/** Password → scan the QR code and type the first code → recovery codes. */
function TurnOn({ onClose }: { onClose: () => void }) {
  const start = useStartTwoFactor();
  const enable = useEnableTwoFactor();
  const [setup, setSetup] = useState<(TwoFactorSetupResponse & { qr: string }) | null>(null);
  const codes = enable.data?.recoveryCodes;

  if (codes) return <RecoveryCodes codes={codes} onDone={onClose} />;

  if (!setup) {
    const submit = (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      start.mutate(field(event, 'password'), {
        onSuccess: (result) =>
          void QRCode.toDataURL(result.otpauthUrl, { margin: 1, width: 220, errorCorrectionLevel: 'M' }).then(
            (qr) => setSetup({ ...result, qr }),
          ),
      });
    };
    return (
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm">
          You’ll need an authenticator app on your phone, such as Aegis, 2FAS, Google Authenticator or your
          password manager. Confirm your password to start.
        </p>
        <PasswordField />
        {start.error && <ErrorText>{start.error.message}</ErrorText>}
        <Actions>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={start.isPending}>
            Continue
          </Button>
        </Actions>
      </form>
    );
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    enable.mutate(field(event, 'code'));
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm">Scan this with your authenticator app, then type the code it shows.</p>
      <img
        src={setup.qr}
        alt="QR code for your authenticator app"
        width={220}
        height={220}
        className="mx-auto rounded-lg bg-white p-2"
      />
      <details className="text-sm">
        <summary className="cursor-pointer text-blue-700 dark:text-sky-400">
          Can’t scan it? Type this key
        </summary>
        <p className="mt-2 font-mono tracking-wider break-all" data-testid="totp-secret">
          {setup.secret.replace(/(.{4})/g, '$1 ').trim()}
        </p>
      </details>
      <CodeField />
      {enable.error && <ErrorText>{enable.error.message}</ErrorText>}
      <Actions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={enable.isPending}>
          Turn on
        </Button>
      </Actions>
    </form>
  );
}

function TurnOff({ onClose }: { onClose: () => void }) {
  const disable = useDisableTwoFactor();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    disable.mutate(
      { password: field(event, 'password'), code: field(event, 'code') },
      { onSuccess: onClose },
    );
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm">Logging in will need only your password again.</p>
      <PasswordField />
      <CodeField label="Code from your app, or a recovery code" allowRecovery />
      {disable.error && <ErrorText>{disable.error.message}</ErrorText>}
      <Actions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="danger" disabled={disable.isPending}>
          Turn off
        </Button>
      </Actions>
    </form>
  );
}

function NewCodes({ onClose }: { onClose: () => void }) {
  const replace = useReplaceRecoveryCodes();
  const codes = replace.data?.recoveryCodes;
  if (codes) return <RecoveryCodes codes={codes} onDone={onClose} />;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    replace.mutate(field(event, 'password'));
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm">You’ll get 10 new codes, and your old ones will stop working.</p>
      <PasswordField />
      {replace.error && <ErrorText>{replace.error.message}</ErrorText>}
      <Actions>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={replace.isPending}>
          Make new codes
        </Button>
      </Actions>
    </form>
  );
}

const titles = {
  on: 'Turn on two-factor login',
  off: 'Turn off two-factor login',
  codes: 'New recovery codes',
};

/** Settings → Two-factor login. */
export function TwoFactorSection({ enabled, codesLeft }: { enabled: boolean; codesLeft: number }) {
  // The dialog unmounts its contents when closed, so each opening starts afresh.
  const [open, setOpen] = useState<keyof typeof titles | null>(null);
  const close = () => setOpen(null);

  return (
    <>
      {enabled ? (
        <div className="space-y-3">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone="green">
              <ShieldCheck className="mr-1 h-3.5 w-3.5" aria-hidden /> On
            </Badge>
            Logging in needs a code from your authenticator app as well as your password.
          </p>
          <p
            className={`text-sm ${codesLeft <= 2 ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400'}`}
          >
            {codesLeft} recovery {codesLeft === 1 ? 'code' : 'codes'} left
            {codesLeft <= 2 && ': make new ones before you run out.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setOpen('codes')}>
              <KeyRound className="h-4 w-4" aria-hidden /> New recovery codes
            </Button>
            <Button variant="ghostDanger" onClick={() => setOpen('off')}>
              <ShieldOff className="h-4 w-4" aria-hidden /> Turn off
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Ask for a code from an authenticator app on your phone as well as your password, so a leaked
            password alone isn’t enough.
          </p>
          <Button variant="primary" onClick={() => setOpen('on')} className="shrink-0">
            <ShieldCheck className="h-4 w-4" aria-hidden /> Turn on
          </Button>
        </div>
      )}

      <Dialog open={open !== null} onClose={close} title={open ? titles[open] : ''}>
        {open === 'on' && <TurnOn onClose={close} />}
        {open === 'off' && <TurnOff onClose={close} />}
        {open === 'codes' && <NewCodes onClose={close} />}
      </Dialog>
    </>
  );
}
